import { PButton } from "@porsche-design-system/components-react"
import { updateInventoryInformationRoute } from "@slfinrtl/ui-bff-routes"
import type {
	BasicPricingValues,
	InventoryInformationChange,
	InventoryInformationEditView,
	InventoryInformationView,
	ModelInformationView,
} from "@slfinrtl/ui-bff-routes"
import { dequal } from "dequal/lite"
import { useCallback, useEffect, useState } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { FormattedMessage } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { getDifference } from "src/common/getDifference/getDifference"
import { removeUndefinedKeys } from "src/common/removeUndefinedKeys"
import { transformValues } from "src/common/transformValues/transformValues"
import type { InventoryInformationFormValues } from "src/components"
import {
	ButtonGroup,
	getInventoryInformationChangeRules,
	inventoryAndIntakeFieldNames,
	InventoryAndIntakeForm,
	mapToInventoryInformationChange,
	mapToInventoryInformationFormValues,
	useConditionAndServiceFields,
	useInventoryAndIntakeFields,
} from "src/components"
import { useModal } from "src/context/modal/useModal"
import { useBffDynamic, useConfig } from "src/hooks"
import { useVehicleDetailsContext } from "src/screens/vehicleDetails/context/VehicleDetailsContext"
import type { InventoryAndIntakeUpdateModalProps } from "./modals/inventoryAndIntakeUpdateModal/InventoryAndIntakeUpdateModal"
import { InventoryAndIntakeUpdateModal } from "./modals/inventoryAndIntakeUpdateModal/InventoryAndIntakeUpdateModal"

interface InventoryAndIntakeFormContainerProps {
	inventoryId: string
	onCancel: () => void
	onSaved: (view: InventoryInformationView) => void
	view: InventoryInformationEditView
}

export const InventoryAndIntakeFormContainer = ({
	inventoryId,
	onCancel,
	onSaved,
	view,
}: InventoryAndIntakeFormContainerProps) => {
	const form = useForm<InventoryInformationFormValues>({
		defaultValues: mapToInventoryInformationFormValues(view.values),
		mode: "onSubmit",
	})

	return (
		<FormProvider {...form}>
			<InventoryAndIntakeInnerFormContainer
				view={view}
				inventoryId={inventoryId}
				onCancel={onCancel}
				onSaved={onSaved}
			/>
		</FormProvider>
	)
}

interface InventoryAndIntakeInnerFormContainerProps {
	inventoryId: string
	onCancel: () => void
	onSaved: (view: InventoryInformationView) => void
	view: InventoryInformationEditView
}

const InventoryAndIntakeInnerFormContainer = ({
	inventoryId,
	onCancel,
	onSaved,
	view,
}: InventoryAndIntakeInnerFormContainerProps) => {
	const [isSaving, setIsSaving] = useState(false)
	const [modelInformationView, setModelInformationView] = useState<ModelInformationView | null>()

	const { subscribe } = useVehicleDetailsContext()
	const config = useConfig()

	useEffect(() => subscribe("modelInformationChanged", setModelInformationView), [subscribe])

	const showUpdateModal = useModal<InventoryAndIntakeUpdateModalProps>(InventoryAndIntakeUpdateModal)
	const conditionFields = useConditionAndServiceFields(false, modelInformationView?.modelYear, view.isElectric)

	const patch = useBffDynamic(updateInventoryInformationRoute, "inline")

	const { handleSubmit, reset, watch } = useFormContext<InventoryInformationFormValues>()
	const values = watch()
	const { stockStatus, vehicleType } = values

	const customerVehicleRelationshipWarningVisible =
		view.values.inventoryAndIntake.stockStatus !== "IN_STOCK" &&
		stockStatus === "IN_STOCK" &&
		vehicleType === "COMMISSION_CAR"

	const inventoryAndIntakeFields = useInventoryAndIntakeFields(values, view, false)

	useEffect(() => {
		const transformedValues = transformValues(getInventoryInformationChangeRules(config), values, config)

		// Reset only inventory & intake values. Leave other stages as they are
		// for now, to make sure that changing a value and then reverting it leads back to the original state.
		const newValues: InventoryInformationFormValues = { ...values }
		Object.values(inventoryAndIntakeFieldNames).forEach((fieldName) => {
			;(newValues[fieldName] as unknown) = transformedValues[fieldName]
		})

		if (!dequal(newValues, values)) {
			reset(newValues, { keepErrors: true, keepFieldsRef: true })
		}
	}, [reset, config, values])

	const onValid = () => {
		const transformedValues = transformValues<InventoryInformationFormValues>(
			getInventoryInformationChangeRules(config),
			values,
			config,
		)
		const inventoryChangeValues = mapToInventoryInformationChange({
			...transformedValues,
			pricingUseCase: values.pricingUseCase,
		})

		const difference = inventoryChangeValues
			? getDifference<InventoryInformationChange>(inventoryChangeValues, view.values)
			: undefined

		if (difference) {
			const conditionAndServiceChanges = difference.conditionAndService
				? removeUndefinedKeys(difference.conditionAndService)
				: undefined

			const shouldShowUpdatePriceMessage = difference.inventoryAndIntake?.vehicleType !== undefined

			// This is needed to make sure that there are no additional required
			// fields (e.g. firstRegistrationDate changes from optional to
			// mandatory when vehicleType changes from NEW to USED)
			const isConditionValid = Object.entries(conditionFields).every(([fieldKey, fieldDefinition]) => {
				const value = (inventoryChangeValues!.conditionAndService as any)[fieldDefinition?.name ?? fieldKey]
				return !fieldDefinition?.required || (value !== undefined && value !== null)
			})

			if (conditionAndServiceChanges || !isConditionValid || shouldShowUpdatePriceMessage) {
				showUpdateModal({
					conditionAndServiceChanges: { conditionAndService: conditionAndServiceChanges },
					currentFormValues: transformedValues,
					inventoryAndIntakeChanges: { inventoryAndIntake: difference.inventoryAndIntake },
					isConditionValid,
					isElectric: view.isElectric,
					modelYear: modelInformationView?.modelYear,
					onSave: save,
					pricingChanges: { pricing: difference.pricing as Partial<BasicPricingValues> },
					showUpdatePriceMessage: shouldShowUpdatePriceMessage,
				})
				return
			}
		}
		if (inventoryChangeValues) {
			save(inventoryChangeValues)
		}
	}

	const save = useCallback(
		(informationChange: InventoryInformationChange) => {
			const patchedValues = informationChange ? getDifference(informationChange, view.values) : undefined

			setIsSaving(true)
			return patch({
				body: patchedValues
					? {
							...patchedValues,
							pricing: {
								...patchedValues.pricing,
								// PricingUseCase must always be sent to the
								// bff
								pricingUseCase: view.values.pricing.pricingUseCase,
							},
						}
					: {},
				pathParams: { inventoryId },
				queryParams: {},
			})
				.then(onSaved)
				.finally(() => setIsSaving(false))
		},
		[inventoryId, onSaved, patch, view.values],
	)

	return (
		<>
			<InventoryAndIntakeForm
				customerVehicleRelationshipWarningVisible={customerVehicleRelationshipWarningVisible}
				fields={inventoryAndIntakeFields}
			/>
			<ButtonGroup>
				<PButton loading={isSaving} onClick={handleSubmit(onValid)}>
					<FormattedMessage {...formMessages.save} />
				</PButton>
				<PButton type="button" variant="secondary" onClick={onCancel}>
					<FormattedMessage {...formMessages.cancel} />
				</PButton>
			</ButtonGroup>
		</>
	)
}
